import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, AlertTriangle, Barcode, Camera } from "lucide-react";
import { api, rd, inputCls, labelCls, CATEGORIES, GENDERS, askPin, printBarcodeLabels } from "./posShared";
import CameraScanner from "./CameraScanner";

const empty = {
  name: "", code: "", barcode: "", brand: "", category: "miniperfume", gender: "unisex",
  size_ml: 0, cost: 0, price: 0, qty: 0, min_qty: 3, image: "", description: "", is_sample: false,
};

export default function Productos({ products, reload }) {
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);

  const filtered = products.filter(
    (p) =>
      p.name.toLowerCase().includes(q.toLowerCase()) ||
      (p.code || "").toLowerCase().includes(q.toLowerCase()) ||
      (p.barcode || "").toLowerCase().includes(q.toLowerCase())
  );

  const save = async (d) => {
    const payload = {
      ...d, size_ml: Number(d.size_ml) || 0, cost: Number(d.cost) || 0,
      price: Number(d.price) || 0, qty: Number(d.qty) || 0, min_qty: Number(d.min_qty) || 0,
    };
    try {
      if (d.id) {
        await api.put(`/pos/products/${d.id}`, payload);
        toast.success("Producto guardado");
        setModal(null);
        reload();
      } else {
        const { data } = await api.post("/pos/products", payload);
        toast.success("Producto guardado");
        setModal(null);
        reload();
        const prod = data || payload;
        // Solo ofrecer generar/imprimir etiqueta si el producto NO trae código de barras propio.
        if (!String(payload.barcode || "").trim()) {
          if (window.confirm("Este producto no tiene código de barras. ¿Generar e imprimir una etiqueta ahora?")) {
            const n = window.prompt("¿Cuántas etiquetas quieres imprimir?", "1");
            if (n !== null) printBarcodeLabels(prod, Number(n) || 1);
          }
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    const pin = await askPin();
    try {
      await api.delete(`/pos/products/${p.id}?pin=${encodeURIComponent(pin)}`);
      toast.success("Producto eliminado");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const label = (p) => {
    const n = window.prompt(`¿Cuántas etiquetas de "${p.name}" quieres imprimir?`, "1");
    if (n === null) return;
    printBarcodeLabels(p, Number(n) || 1);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl">Productos</h1>
        <button data-testid="pos-add-product" onClick={() => setModal({ ...empty })}
          className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-rose transition">
          <Plus size={15} /> Nuevo producto
        </button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input data-testid="pos-product-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, código o código de barras..." className={`${inputCls} pl-9`} />
      </div>

      <div className="overflow-x-auto bg-white border border-neutral-200 rounded-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
              <th className="px-4 py-3">Producto</th>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3 text-right">Costo</th>
              <th className="px-4 py-3 text-right">Precio</th>
              <th className="px-4 py-3 text-right">Ganancia</th>
              <th className="px-4 py-3 text-center">Stock</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const low = p.qty > 0 && p.qty <= p.min_qty;
              const out = p.qty <= 0;
              return (
                <tr key={p.id} data-testid={`pos-product-row-${p.id}`} className="border-b border-neutral-50 hover:bg-neutral-50/60">
                  <td className="px-4 py-3">
                    <div className="font-medium text-espresso">{p.name}</div>
                    <div className="text-xs text-neutral-400">{p.code} {p.brand && `· ${p.brand}`}</div>
                  </td>
                  <td className="px-4 py-3 capitalize text-neutral-600">{p.category}</td>
                  <td className="px-4 py-3 text-right text-neutral-600">{rd(p.cost)}</td>
                  <td className="px-4 py-3 text-right font-medium">{rd(p.price)}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">{rd(p.profit)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${out ? "bg-red-100 text-red-600" : low ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-600"}`}>
                      {(low || out) && <AlertTriangle size={11} />} {p.qty}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button data-testid={`pos-label-product-${p.id}`} onClick={() => label(p)} title="Imprimir etiqueta con código de barras" className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><Barcode size={14} /></button>
                      <button data-testid={`pos-edit-product-${p.id}`} onClick={() => setModal({ ...p })} className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><Pencil size={14} /></button>
                      <button data-testid={`pos-del-product-${p.id}`} onClick={() => del(p)} className="p-1.5 border border-neutral-200 rounded-lg text-neutral-400 hover:text-red-500 hover:border-red-300"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">Sin productos.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && <ProductModal data={modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function ProductModal({ data, onClose, onSave }) {
  const [d, setD] = useState(data);
  const [camera, setCamera] = useState(false);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const profit = (Number(d.price) || 0) - (Number(d.cost) || 0);

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 sticky top-0 bg-white">
          <h2 className="font-serif text-2xl">{d.id ? "Editar" : "Nuevo"} producto</h2>
          <button onClick={onClose} className="text-neutral-400 text-xl">✕</button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          <div className="col-span-2"><label className={labelCls}>Nombre</label><input data-testid="pos-pf-name" value={d.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Código interno</label><input data-testid="pos-pf-code" value={d.code} onChange={(e) => set("code", e.target.value)} className={inputCls} /></div>
          <div className="col-span-2"><label className={labelCls}>Código de barras del empaque</label>
            <div className="relative">
              <input data-testid="pos-pf-barcode" value={d.barcode} onChange={(e) => set("barcode", e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} placeholder="Escanea el código impreso en el producto/empaque..." className={`${inputCls} pr-16`} />
              <button type="button" data-testid="pos-pf-barcode-camera" onClick={() => setCamera(true)} title="Escanear con la cámara" className="absolute right-9 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-rose"><Camera size={15} /></button>
              <Barcode size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-rose" />
            </div>
            <p className="text-[11px] text-neutral-400 mt-1">Usa el lector o la cámara para capturar el código ya impreso. Si lo dejas vacío, podrás generar una etiqueta al guardar.</p>
          </div>
          <div><label className={labelCls}>Marca</label><input value={d.brand} onChange={(e) => set("brand", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Categoría</label><select value={d.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>{CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></div>
          <div><label className={labelCls}>Género</label><select value={d.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls}>{GENDERS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}</select></div>
          <div><label className={labelCls}>Tamaño (ml)</label><input type="number" value={d.size_ml} onChange={(e) => set("size_ml", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Costo de compra</label><input data-testid="pos-pf-cost" type="number" value={d.cost} onChange={(e) => set("cost", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Precio de venta</label><input data-testid="pos-pf-price" type="number" value={d.price} onChange={(e) => set("price", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Cantidad disponible</label><input data-testid="pos-pf-qty" type="number" value={d.qty} onChange={(e) => set("qty", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Cantidad mínima</label><input type="number" value={d.min_qty} onChange={(e) => set("min_qty", e.target.value)} className={inputCls} /></div>
          <div className="col-span-2"><label className={labelCls}>Descripción</label><textarea rows={2} value={d.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={d.is_sample} onChange={(e) => set("is_sample", e.target.checked)} /> Producto usado como muestra / probador</label>
          <div className="col-span-2 text-sm text-neutral-500">Ganancia por unidad: <strong className="text-emerald-600">{rd(profit)}</strong></div>
          <button data-testid="pos-save-product" onClick={() => onSave(d)} className="col-span-2 bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition">Guardar producto</button>
        </div>
      </div>
      {camera && <CameraScanner onDetect={(code) => set("barcode", String(code || "").trim())} onClose={() => setCamera(false)} />}
    </div>
  );
}
